import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Warning, Delete, Info } from '@mui/icons-material';
import React from 'react';

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  onClose?: () => void; // Alias for onCancel
  loading?: boolean;
  isLoading?: boolean; // Alias for loading
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  confirmColor,
  onConfirm,
  onCancel,
  onClose,
  loading: externalLoading,
  isLoading: externalIsLoading,
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading ?? externalIsLoading ?? internalLoading;
  const handleClose = onClose ?? onCancel;

  const colors = {
    danger: { bg: '#FEE2E2', color: '#DC2626', icon: <Delete /> },
    warning: { bg: '#FEF3C7', color: '#D97706', icon: <Warning /> },
    info: { bg: '#DBEAFE', color: '#2563EB', icon: <Info /> },
  };

  const { bg, color, icon } = colors[variant];

  // Determine button color based on variant or explicit confirmColor
  const buttonColor = confirmColor ?? (variant === 'danger' ? 'error' : variant === 'warning' ? 'warning' : 'primary');

  // Reset loading state when dialog closes
  useEffect(() => {
    if (!open) {
      setInternalLoading(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      await onConfirm();
    } catch (error) {
      console.error('Confirm action failed:', error);
    } finally {
      setInternalLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      TransitionComponent={SlideTransition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {typeof message === 'string' ? (
          <DialogContentText
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
            }}
          >
            {message}
          </DialogContentText>
        ) : (
          <Box sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{message}</Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          color="inherit"
          sx={{
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          color={buttonColor}
          sx={{
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 500,
            ...(variant === 'danger' && !confirmColor && {
              backgroundColor: color,
              '&:hover': {
                backgroundColor: color,
                opacity: 0.9,
              },
            }),
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            confirmText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
